import { Head, Link } from "@inertiajs/react"
import type { Listing } from "@/types"
import DashboardLayout from "@/components/dashboard/layout"
import ListingCard from "@/components/dashboard/listings/listing-card"
import ListingRow from "@/components/dashboard/listings/listing-row"
import IndexToolbar from "@/components/dashboard/index-toolbar"
import Pagination from "@/components/pagination"
import { useViewPreference } from "@/hooks/use-view-preference"
import { Button } from "@/components/ui/button"

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

const LISTING_FILTERS = [
  {
    label: "All statuses",
    param: "status",
    options: [
      { value: "draft", label: "Draft" },
      { value: "active", label: "Active" },
      { value: "archived", label: "Archived" },
    ],
  },
  {
    label: "All types",
    param: "listing_type",
    options: [
      { value: "sale", label: "For sale" },
      { value: "long_let", label: "Long let" },
      { value: "short_let", label: "Short let" },
      { value: "holiday_let", label: "Holiday let" },
    ],
  },
]

const LISTING_SORT_OPTIONS = [
  { value: "updated_at", label: "Last updated" },
  { value: "created_at", label: "Date added" },
  { value: "address", label: "Address" },
  { value: "price", label: "Price" },
]

export default function ListingsIndex({
  listings,
  pagination,
  filters = {},
}: {
  listings: Listing[]
  pagination: PaginationMeta
  current_role: string
  filters: Record<string, string>
}) {
  const [view, setView] = useViewPreference("grid")

  return (
    <DashboardLayout>
      <Head title="Listings | QuiteRight" />

      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
            Listings
          </h1>
          <p className="mt-1 text-warm-grey">
            Manage your property listings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="secondary">
            <Link href="/dashboard/listings/new?manual=1">Add manually</Link>
          </Button>
          <Button asChild className="bg-red text-white hover:bg-red-deep">
            <Link href="/dashboard/listings/new">Import listing</Link>
          </Button>
        </div>
      </div>

      {pagination.count === 0 && !hasActiveFilters(filters) ? (
        <EmptyState />
      ) : (
        <>
          <IndexToolbar
            filters={LISTING_FILTERS}
            sortOptions={LISTING_SORT_OPTIONS}
            currentFilters={filters}
            currentSort={filters.sort || "updated_at"}
            currentDirection={filters.direction || "desc"}
            view={view}
            onViewChange={setView}
            baseUrl="/dashboard/listings"
            count={pagination.count}
            noun="listing"
          />

          {listings.length === 0 ? (
            <NoResults />
          ) : view === "grid" ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {listings.map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {listings.map((listing) => (
                <ListingRow key={listing.id} listing={listing} />
              ))}
            </div>
          )}

          <Pagination
            pagination={pagination}
            baseUrl="/dashboard/listings"
            params={filters}
          />
        </>
      )}
    </DashboardLayout>
  )
}

function hasActiveFilters(filters: Record<string, string>): boolean {
  return Object.entries(filters).some(([key, val]) => val && key !== "sort" && key !== "direction")
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-stone/60 bg-white py-16 px-8 text-center">
      <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-stone/20">
        <svg className="size-8 text-warm-grey" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
        </svg>
      </div>
      <h2 className="mb-2 font-display text-xl font-semibold text-charcoal">
        No listings yet
      </h2>
      <p className="mb-6 max-w-sm text-warm-grey">
        Paste a listing URL or add one manually to start creating scroll-stopping video ads.
      </p>
      <div className="flex items-center gap-2">
        <Button asChild variant="secondary">
          <Link href="/dashboard/listings/new?manual=1">Add manually</Link>
        </Button>
        <Button asChild className="bg-red text-white hover:bg-red-deep">
          <Link href="/dashboard/listings/new">Import listing</Link>
        </Button>
      </div>
    </div>
  )
}

function NoResults() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-stone/60 bg-white py-12 px-8 text-center">
      <p className="text-sm text-warm-grey">No listings match your filters.</p>
    </div>
  )
}
